import {
  useFormState,
  useStateObject,
  isObjectEmptyData,
} from 'holistic-resources';
import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import DefaultActionButtons from 'components/DefaultActionButtons';
import Modal from 'components/Modal';
import TextField from 'components/formFields/TextField';
import {
  showSuccessFeedbackBar,
  showErrorFeedbackBar,
} from 'reducers/ui/uiSlice';
import { updateVisitor } from 'services/ApiService';
import {
  UPDATE_SUCCESS,
  UPDATE_ERROR,
} from 'shared/constants/feedbackMessages';
import { validateFormRequiriedFields } from 'shared/helpers/common';

const DEFAULT_CONTACT = {
  primary: true,
  active: true,
  dateCreated: new Date().getTime(),
  type: 'OTHER',
};

const getContactList = (
  contactList = [],
  primaryIndex = -1,
  newContact = null,
) => {
  const list = contactList.map((item, index) => ({
    ...item,
    primary: primaryIndex === index,
  }));

  if (newContact) {
    list.push(newContact);
  }

  return [...list];
};

export default function ContactTabEditModal({
  isOpen,
  setModalState,
  primaryEmailIndex,
  primaryPhoneIndex,
  setUserData,
  userData = {},
  moduleConfig = {},
}) {
  const dispatch = useDispatch();
  const [phone, setPhone] = useState({});
  const [fieldsConfig, setFieldsConfig] = useState({});

  const {
    formValue: email,
    setFormValue: setEmail,
    formChangeHandler: onEmailChangeHandler,
  } = useFormState({});
  const { value: formErrorMessages, updateValue: setFormErrorMessages } =
    useStateObject({});

  useEffect(() => {
    setFieldsConfig({
      number: {
        required: moduleConfig?.['Client-Client-view-contactNumber-mandatory'],
        visible: moduleConfig?.['Client-Client-view-contactNumber-visible'],
        value: phone?.visibleNumber,
        fieldName: 'number',
        label: 'Contact number',
      },
      email: {
        required: moduleConfig?.['Client-Client-view-email-mandatory'],
        visible: moduleConfig?.['Client-Client-view-email-visible'],
        value: email?.email,
        fieldName: 'email',
        label: 'Contact email',
      },
    });
  }, [phone, email, moduleConfig]);

  const getUpdatedEmails = () => {
    const { emailAddress: currentEmails = [] } = userData;

    if (primaryEmailIndex === -1) {
      return getContactList([...currentEmails], primaryEmailIndex, {
        ...email,
      });
    }

    currentEmails[primaryEmailIndex] = { ...email };
    return getContactList([...currentEmails], primaryEmailIndex);
  };

  const getUpdatedPhoneNumbers = () => {
    const { phone: currentPhoneNumbers = [] } = userData;

    if (primaryPhoneIndex === -1) {
      return getContactList([...currentPhoneNumbers], primaryPhoneIndex, {
        ...phone,
      });
    }

    currentPhoneNumbers[primaryPhoneIndex] = { ...phone };
    return getContactList([...currentPhoneNumbers], primaryPhoneIndex);
  };

  useEffect(() => {
    if (primaryEmailIndex === -1) {
      setEmail({ ...DEFAULT_CONTACT });
      return;
    }

    if (userData?.emailAddress?.[primaryEmailIndex]) {
      setEmail({
        ...userData?.emailAddress?.[primaryEmailIndex],
        active: true,
        primary: true,
      });      
    }
  }, [userData, primaryEmailIndex, setEmail]);

  useEffect(() => {
    if (primaryPhoneIndex === -1) {
      setPhone({ ...DEFAULT_CONTACT });
      return;
    }

    if (userData?.phone?.[primaryPhoneIndex]) {
      setPhone({
        ...userData?.phone?.[primaryPhoneIndex],
        active: true,
        primary: true,
      });      
    }
  }, [userData, primaryPhoneIndex, setPhone]);

  const onPhoneChangeHandler = ({ target: { value } }) => {
    setPhone((currentData) => ({
      ...currentData,
      number: value.replace?.(/\D/g, ''),
      visibleNumber: value,
    }));
  };

  const closeModal = () => {
    setModalState(false);
  };

  const onCancel = () => {
    closeModal();
  };

  const onSave = async () => {
    const isFormValid = validateFormRequiriedFields(
      setFormErrorMessages,
      fieldsConfig,
    );

    if (!isFormValid) {
      return;
    }

    const updatedEmails = getUpdatedEmails();
    const updatedPhoneNumbers = getUpdatedPhoneNumbers();

    try {
      const { data } = await updateVisitor({
        phone: [...updatedPhoneNumbers],
        emailAddress: [...updatedEmails],
        _id: userData?._id, // eslint-disable-line no-underscore-dangle
      });
      setUserData?.(data);
      closeModal();
      dispatch(showSuccessFeedbackBar(UPDATE_SUCCESS));
    } catch (error) {
      dispatch(showErrorFeedbackBar(UPDATE_ERROR));
      console.error(error);
    }
  };

  return (
    <Modal
      show={isOpen}
      onClose={() => setModalState(false)}
      header="Edit Contact"
      body={
        <div className="flex flex-col">
          <TextField
            isVisible={fieldsConfig?.number?.visible}
            isRequired={fieldsConfig?.number?.required}
            label={fieldsConfig?.number?.label}
            name={fieldsConfig?.number?.fieldName}
            setFormErrorMessages={setFormErrorMessages}
            errorMessage={formErrorMessages?.number}
            value={phone?.visibleNumber}
            onChange={onPhoneChangeHandler}
          />
          <TextField
            isVisible={fieldsConfig?.email?.visible}
            isRequired={fieldsConfig?.email?.required}
            label={fieldsConfig?.email?.label}
            name={fieldsConfig?.email?.fieldName}
            setFormErrorMessages={setFormErrorMessages}
            errorMessage={formErrorMessages?.email}
            value={email?.email}
            onChange={onEmailChangeHandler}
          />
        </div>
      }
      footer={
        <DefaultActionButtons
          onSecondaryButtonClick={onCancel}
          onPrimaryButtonClick={onSave}
          isPrimaryButtonDisabled={!isObjectEmptyData(formErrorMessages)}
        />
      }
    />
  );
}
